
export interface IToastResult {
	actionClicked: boolean;
}

/**
 * Created by tom on 7/04/17.
 */
export interface IToastService {
	DURATION_LONG: number;
	DURATION_SHORT: number;

	/**
	 *
	 * @param message - the message in the toast
	 * @param duration - number of milliseconds the toast should be visible
	 * @param extraClass - extra CSS class for additional styling
	 * @param action - callback when the toast is clicked
	 * @param uniqueId - if supplied, only one toast will be allowed in the queue with this id
	 */
	showToast(message : string, duration?: number, extraClass?: string, action?: IToastAction, uniqueId?: string) : ng.IPromise<IToastResult>;
	showToastObj(toast: IToast) : ng.IPromise<IToastResult>;
	toastDefaults(): { extraClass: string, duration: number }
	cancelToasts(toast: IToast);
	persistentToast: IToast;
	element: JQuery;
	closeOnClick();
}

export const TOAST_DURATION_SNACKBAR = 6000;
export const TOAST_DURATION_LONG = 3000;
export const TOAST_DURATION_SHORT = 1500;

/**
 * [ToastService]
 *
 * Android style Toasts
 *
 * inject 'toastService' into you angular components and call
 * [showToast] to display an alert to the user.
 *
 * The service manages a queue of toasts, display them first in first out, one at a time.
 *
 * The toast stylesheet is in _toast.scss
 */
export class ToastService implements IToastService {
	public DURATION_LONG = TOAST_DURATION_LONG;
	public DURATION_SHORT = TOAST_DURATION_SHORT;

	public static $inject = ['$timeout', '$q'];
	protected queue: IToast[] = [];
	protected toastEl : Element = null;
	protected $actionEl;
	protected $toastEl: JQuery;
	protected results: Map<IToast, ng.IDeferred<IToastResult>> = new Map();

	constructor(protected $timeout : ng.ITimeoutService, protected $q: ng.IQService) {
		this.toastEl = document.querySelector('.toast');
		if (!this.toastEl) {
			this.toastEl = document.createElement('div');
			document.querySelector('body').appendChild(this.toastEl);
		} else {
			this.toastEl.innerHTML = '';
		}
		this.$toastEl = angular.element(this.toastEl)
			.addClass('toast raised alert top');
		if (!this.$actionEl) {
			const actionEl = document.createElement('button');
			actionEl.setAttribute('data-action', 'toastAction');
			this.$actionEl = angular.element(actionEl).addClass('flat flat-primary');
		}

	}

	protected currentToast: IToast = null;
	protected _persistentToast: IToast = null;

	/**
	 *
	 * @param message
	 * @param duration
	 * @param extraClass - an optional background colour, see the type below for the list, these are all defined in _colors.scss
	 * @param action
	 * @param uniqueId - if truthy, only one toast will be allowed in the queue with this id
	 */
	showToast(message : string,
			  duration : number = this.DURATION_LONG,
			  extraClass : string = '',
			  action: IToastAction = null,
			  uniqueId: string = null): ng.IPromise<IToastResult> {
		return this.showToastObj({
			message,
			duration,
			extraClass,
			action,
			uniqueId
		});
	}

	toastDefaults() {
		return {
			extraClass: 'toast-gray',
			duration: this.DURATION_LONG
		}
	}

	cancelToasts(toast: IToast) {
		const hasMessage = !!toast.message;
		const hasUniqueId = !!toast.uniqueId;

		let filterFn: (t: IToast) => boolean;

		if (hasMessage && hasUniqueId) {
			filterFn = t => !(t.message === toast.message && t.uniqueId === toast.uniqueId)
		} else if (hasMessage) {
			filterFn = t => !(t.message === toast.message && !t.uniqueId)
		} else if (hasUniqueId) {
			filterFn = t => t.uniqueId !== toast.uniqueId;
		} else {
			filterFn = (_) => true;
		}

		if (this.currentToast && !filterFn(this.currentToast)) {
			this.$timeout.cancel(this.currentTimeout);
			this.hideToast();
			this.running = false;
		}

		this.queue = this.queue.filter(filterFn);

		if (this.queue.length && !this.running) {
			this.doShowToasts();
		}
	}

	set persistentToast(toast: IToast) {
		this._persistentToast = toast;

		if (toast) {
			toast.persistent = true;
			this.doShowToasts();
		} else if (!this.running) {
			this.hideToast();
		}
	}

	get persistentToast(): IToast {
		return this._persistentToast;
	}

	showToastObj(toast: IToast): ng.IPromise<IToastResult> {
		const nextToast = this.queue.length && this.queue[this.queue.length - 1];
		const nextMessage = nextToast && nextToast.message;
		const currentMessage = this.currentToast && this.currentToast.message;

		if (toast.message === nextMessage) {
			return this.results.get(nextToast).promise;
		}

		if (toast.message === currentMessage) {
			return this.results.get(this.currentToast).promise;
		}

		const deferred = this.$q.defer<IToastResult>();
		this.results.set(toast, deferred)

		if (toast.uniqueId) {
			this.queue.filter(toast => toast.uniqueId === toast.uniqueId)
				.forEach(toast => {
					this.results.get(toast).reject('cancelled');
				});

			this.queue = this.queue.filter(toast => toast.uniqueId !== toast.uniqueId);

			if (this.sameUniqueIdAsCurrentMessage(toast.uniqueId)) {
				this.$timeout.cancel(this.currentTimeout);
				this.hideToast();
				this.running = false;
				this.queue.unshift(toast);
				this.doShowToasts();
				return deferred.promise;
			}
		}

		this.queue.push(toast);
		this.doShowToasts();

		return deferred.promise;
	}

	protected sameUniqueIdAsCurrentMessage(id: string) {
		if (!id) return false;

		const currentToast = this.currentToast || { uniqueId: undefined };
		const {uniqueId} = currentToast;
		return id === uniqueId;
	}

	protected currentTimeout: ng.IPromise<any> = null;

	protected running : boolean = false;
	protected doShowToasts() {
		if (this.running) return;

		if (this.queue.length) {
			let toast = this.queue.pop();
			this.running = true;
			this.currentToast = toast;
			let callback = (actionClicked: boolean = false) => {
				this.$toastEl.removeClass('cs-' + toast.extraClass);
				this.results.get(toast).resolve({ actionClicked })
				if (this.queue.length) {
					let next = this.queue.pop();
					this.currentToast = next;
					this.displayToast(next, callback);
					this.$timeout(callback, next.duration);
					toast = next;
				} else  {
					this.currentToast = undefined;
					this.running = false;

					if (this.persistentToast) {
						this.displayToast(this.persistentToast, null);
					} else {
						this.hideToast();
					}
				}
			};
			this.displayToast(toast, callback);
			this.currentTimeout = this.$timeout(callback, toast.duration);
		} else if (this.persistentToast) {
			this.displayToast(this.persistentToast, null);
		}
	}

	protected displayToast(toast : IToast, callback: (boolean?) => void) {
		console.log("Displaying toast", toast);
		this.$toastEl.html(toast.message)
			.addClass('in')

		if (toast.extraClass) {
			this.$toastEl.addClass('cs-' + toast.extraClass);
		}

		if (toast.persistent) {
			this.$toastEl.addClass('persistent-toast');
			toast.action = toast.action || {
				onClick: () => {
					this.hideToast();
				},
				label: 'Dismiss'
			};
		}

		if (toast.action) {
			this.$actionEl.html(toast.action.label);
			this.$actionEl.off('click').on('click', () => {
				// this.hideToast();
				// this.results.get(toast).resolve({ actionClicked: true })
				if (this.currentTimeout) this.$timeout.cancel(this.currentTimeout);
				toast.action.onClick();
				if (callback) {
					callback(true);
				}
			});
			this.$toastEl.append(this.$actionEl);
		}

		/*
		const element: HTMLElement = this.$toastEl[0];
		// The following is to calculate the new bottom offset of an element
		// taking into account the height of the .map-pane-bottom element.
		// This could be used for other things, so may be good for refactoring.
		//
		// Used APIs work in all major browsers:
		// https://www.caniuse.com/#search=getComputedStyle
		// https://www.caniuse.com/#search=querySelector
		const style = window.getComputedStyle(element);
		const mapPane = document.querySelector('.map-pane-bottom');
		const mapPaneStyle = window.getComputedStyle(mapPane);
		const mapPaneHeightPx = Number(mapPaneStyle.height.substring(0, mapPaneStyle.height.length - 2));
		const bottom = Number(element.dataset.moved) || Number(style.bottom.substring(0, style.bottom.length - 2));
		const newBottom = bottom + mapPaneHeightPx;
		element.style.bottom = `${newBottom}px`;
		element.dataset.moved = '' + bottom;
		*/
	}

	protected hideToast() {
		console.log("Hiding toasts");
		this.$toastEl.removeClass('in');
	}

	get element() {
		return this.$toastEl;
	}

	closeOnClick() {
		this.$toastEl.off('click').on('click', () => {
			this.hideToast();
		});
	}
}

/*
export type AppColour = "chelsea-cucumber"
	| "chestnut-rose"
	| "cod-gray"
	| "danube"
	| "emperor"
	| "everglade"
	| "fern-green"
	| "ocean-green"
	| "mantis"
	| "mercury"
	| "mine-shaft"
	| "stromboli"
	| "sushi"
	| "gray";
*/

export interface IToastAction {
	label: string;
	onClick: () => void;
}

export interface IToast {
	message: string;
	duration: number; // milliseconds
	extraClass : string;
	action?: IToastAction;
	uniqueId?: string;
	persistent?: boolean;
}
